import { createFileRoute } from '@tanstack/react-router'
import { useAtomValue } from 'jotai'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type DateRange,
  DynamicStats,
  type InsistencesGroupHistory,
  type InsistencesGroupHistoryInput,
  type InsistencesGroupInput,
  type InsistencesGroupStats,
  type InsistencesGroupStatsFilters,
  InsistencesGroupingCriteria,
} from 'types'

import DateRangeFilterDialog from '@/components/filters/date-range-filter-dialog'
import {
  useDynamicStats,
  useDynamicStatsOldestComputedAt,
} from '@/stores/dynamic-stats.atom'
import { portfolioFilterAtom } from '@/stores/portfolio-filter.atom'

import {
  type AnalysisFilters,
  analysisFiltersSchema,
} from '../-components/analysis-filters.schema'
import AnalysisRightBar from '../-components/analysis-right-bar'
import SectionHeader from '../../-components/section-layout/section-header'
import AccountManagerRankingTable from './-components/account-manager-ranking-table'
import InsistencesByAmGraph from './-components/insistences-by-am-graph'
import PerformanceCards from './-components/performance-cards'
import SuccessRateByAmGraph from './-components/success-rate-by-am-graph'
import SuccessRateEvolutionGraph from './-components/success-rate-evolution-graph'
import TreatmentTimeByAmGraph from './-components/treatment-time-by-am-graph'

export const Route = createFileRoute('/app/analysis/performance/')({
  component: RouteComponent,
  validateSearch: analysisFiltersSchema,
})

function RouteComponent() {
  const { t } = useTranslation()
  const navigate = Route.useNavigate()
  const divToExportRef = useRef(null)
  const search = Route.useSearch()
  const portfolioUserUuid = useAtomValue(portfolioFilterAtom)

  const commonFilters = {
    debtorCompanyCountryCodes: search.debtorCompanyCountryCodes,
    groupUuids: search.groupUuids,
    insuranceCompanies: search.insuranceCompanies,
    insuredCompanyCountryCodes: search.insuredCompanyCountryCodes,
    policyUuids: search.policyUuids,
    portfolioUserUuid,
  }

  const insistencesFilters: InsistencesGroupStatsFilters = {
    ...commonFilters,
    dateRange: search.dateRange,
  }

  const insistencesGroupInput: InsistencesGroupInput = {
    filters: insistencesFilters,
    items: ['none', InsistencesGroupingCriteria.ByAccountManager],
  }

  const insistencesGroupHistoryInput: InsistencesGroupHistoryInput = {
    filters: commonFilters,
    items: [
      'none',
      InsistencesGroupingCriteria.ByAccountManager,
      InsistencesGroupingCriteria.ByIntent,
    ],
    intervals: {
      dateRange: search.dateRange,
      intervalDays: 30,
    },
  }

  const insistencesGroupStatsResponse = useDynamicStats(
    DynamicStats.InsistencesGroup,
    insistencesGroupInput,
  )
  const insistencesGroupStats: InsistencesGroupStats | undefined =
    insistencesGroupStatsResponse?.state === 'resolved'
      ? insistencesGroupStatsResponse.data
      : undefined

  const insistencesGroupHistoryResponse = useDynamicStats(
    DynamicStats.InsistencesGroupHistory,
    insistencesGroupHistoryInput,
  )
  const insistencesGroupHistory: InsistencesGroupHistory | undefined =
    insistencesGroupHistoryResponse?.state === 'resolved'
      ? insistencesGroupHistoryResponse.data
      : undefined

  const lastUpdate = useDynamicStatsOldestComputedAt([
    insistencesGroupStatsResponse,
    insistencesGroupHistoryResponse,
  ])

  const setFilters = (filters: AnalysisFilters) =>
    navigate({ search: (prev) => ({ ...prev, ...filters }) })

  const setDateRangeFilter = (dateRange: DateRange | undefined) =>
    navigate({ search: (prev) => ({ ...prev, dateRange }) })

  return (
    <div className="flex grow flex-col divide-y">
      <SectionHeader
        isSecondary
        title={t('analysis.performance.title')}
        subtitle={lastUpdate}
      >
        <AnalysisRightBar
          divToExportRef={divToExportRef}
          filters={search}
          setFilters={setFilters}
          filename="performance-analysis"
        >
          <DateRangeFilterDialog
            value={search.dateRange}
            onChange={setDateRangeFilter}
            className="max-w-56"
          />
        </AnalysisRightBar>
      </SectionHeader>
      <div
        className="flex min-w-0 flex-col gap-2 overflow-x-hidden overflow-y-auto p-2"
        ref={divToExportRef}
      >
        <PerformanceCards stats={insistencesGroupStats?.['none']?.data} />
        <SuccessRateEvolutionGraph history={insistencesGroupHistory} />
        <AccountManagerRankingTable
          amGroups={
            insistencesGroupStats?.[
              InsistencesGroupingCriteria.ByAccountManager
            ]
          }
        />
        <div className="flex w-full min-w-0 flex-col gap-2 2xl:flex-row 2xl:items-stretch">
          <InsistencesByAmGraph history={insistencesGroupHistory} />
          <SuccessRateByAmGraph history={insistencesGroupHistory} />
        </div>
        <div className="flex w-full min-w-0 flex-col gap-2 2xl:flex-row 2xl:items-stretch">
          <TreatmentTimeByAmGraph history={insistencesGroupHistory} />
        </div>
      </div>
    </div>
  )
}
